// The ticket-list status filter — ONE implementation of "which statuses am I looking at", shared by
// the API and the web app on purpose (same reasoning as auDate.ts): the checkbox list the user ticks
// (apps/web TicketsPage.tsx), the `?status=` value that goes in the URL, and the query the list
// endpoint builds (apps/api tickets.service.ts) must all agree on what a selection means. Two
// implementations that disagree is exactly the bug this file exists to end.
//
// Pure, no imports, so the env-free unit-test harness can prove what it accepts and rejects by
// CALLING it.
//
// ── The rules, stated once ───────────────────────────────────────────────────────────────────────
//
//  1. An EMPTY selection means ALL statuses. It is not "show nothing". The list endpoint adds no
//     status clause at all in that case, which is exactly what it did before this filter existed —
//     so the page's default view is unchanged.
//  2. The URL is the single source of truth for the selection, so a filtered view can be bookmarked,
//     shared and survive a refresh. `?status=NEW,OPEN`; the key is absent when nothing is ticked.
//  3. ⛔ A status filter NARROWS what is already visible to the actor. It can never widen it. That
//     is enforced on the server (the scope clause is AND-ed, never replaced) — see
//     ticketListWhere.logic.ts — and asserted in tests. Nothing here grants access to anything.
/** Every TicketStatus, in prisma/schema.prisma order. A test pins this list to the schema.
 *
 * ⛔ This array — not the database's physical enum order — is the DISPLAY order everywhere: the
 * filter checkboxes, the status dropdowns and the `?status=` encoding all read it. `IN_PROGRESS`
 * was appended to the Postgres enum (20260929190000_ticket_status_in_progress) but belongs after
 * OPEN for a reader, so the two orders differ on purpose. Nothing orders a query by this column. */
export const TICKET_STATUS_OPTIONS = ['NEW', 'OPEN', 'IN_PROGRESS', 'WAITING_ON_CLIENT', 'RESOLVED', 'CLOSED', 'ABORTED'];
/**
 * The FINISHED statuses — work nobody is coming back to.
 *
 * ⛔ CLOSED **and** ABORTED. Josh asked to see "all ticket statuses EXCEPT the closed ones"; ABORTED
 * is the other terminal status (the objective was abandoned), so the shortcut hides both. Leaving
 * ABORTED in would have put dead tickets in a list whose whole point is live work.
 *
 * ⛔ RESOLVED is deliberately NOT here. A client can reopen a resolved ticket for 7 days
 * (portalReopen), so it is still live work and the shortcut keeps it. This is a DIFFERENT set from
 * `TERMINAL_STATUS` in TicketsPage.tsx, which drives due-date colouring and does include RESOLVED —
 * a resolved ticket is not waiting on anybody, so its due date stops being urgent, but it has not
 * finished either. Same word, two honest meanings; they are kept apart on purpose.
 */
export const FINISHED_TICKET_STATUSES = ['CLOSED', 'ABORTED'];
/** Everything that is not finished — what the "Hide closed" shortcut selects. Derived, never typed twice. */
export const OPEN_WORK_TICKET_STATUSES = TICKET_STATUS_OPTIONS.filter((s) => !FINISHED_TICKET_STATUSES.includes(s));
const RANK = new Map(TICKET_STATUS_OPTIONS.map((s, i) => [s, i]));
/** Known values only, de-duplicated, in schema order — so the same ticks always encode the same URL. */
function normalise(values) {
    const seen = new Set();
    for (const v of values) {
        const s = v.trim().toUpperCase();
        if (RANK.has(s))
            seen.add(s);
    }
    return [...seen].sort((a, b) => RANK.get(a) - RANK.get(b));
}
/**
 * Read a selection out of a `?status=` value.
 *
 * Accepts `NEW,OPEN`, a single `NEW`, stray whitespace and lower case. An unknown value is DROPPED
 * rather than throwing: a stale or hand-edited bookmark must still render the page, not a blank
 * screen. (The API is stricter — an unknown status there is a 400, so a bad filter can never
 * silently match nothing server-side.)
 */
export function parseTicketStatusFilter(raw) {
    if (!raw)
        return [];
    return normalise(raw.split(','));
}
/** Write a selection back to a `?status=` value. `''` means "not filtered" — the caller drops the key. */
export function encodeTicketStatusFilter(selection) {
    return normalise(selection).join(',');
}
/** Is this selection exactly "everything except the finished statuses"? Drives the shortcut's on/off look. */
export function isOpenWorkOnly(selection) {
    return encodeTicketStatusFilter(selection) === encodeTicketStatusFilter(OPEN_WORK_TICKET_STATUSES);
}
//# sourceMappingURL=ticketStatusFilter.js.map